import { RITIRO_IN_SEDE } from "@/utilities/ddtStandard";

/**
 * [sc-48351] Le regole dei DDT di PLUS: consegna, ritiro in sede e reso.
 *
 * Stanno qui e non in `DdtModal.vue` perché la modale non si carica in un
 * test: contiene la clausola `import.meta` morta di Vite (vedi il commento di
 * `packagingFee.js`). Qui sono funzioni pure, e la modale le usa e basta.
 */

/**
 * Le causali della modale completa. Il reso l'API lo sapeva già fare; la
 * modale no, da quando crmstart è nato da un CRM che non l'aveva. Riprende
 * quello di Barcio.
 */
export const CAUSALI_DDT = Object.freeze([
  Object.freeze({
    value: "consegna",
    label: "Consegna",
    sub: "al cliente",
    icon: "fa-solid fa-truck",
  }),
  Object.freeze({
    value: "ritiro",
    label: "Ritiro",
    sub: "in sede",
    icon: "fa-solid fa-box-open",
  }),
  Object.freeze({
    value: "reso",
    label: "Reso",
    sub: "merce che torna",
    icon: "fa-solid fa-rotate-left",
  }),
]);

/**
 * Quanto di una riga si può ancora rendere: uscito (consegne e ritiri) meno
 * quello che è già tornato. I resi si accumulano, un documento per volta.
 */
export function rendibile(riga) {
  const uscito = Math.floor(Number(riga?.deliveredQty) || 0);
  const reso = Math.floor(Number(riga?.returnedQty) || 0);
  return Math.max(0, uscito - reso);
}

/** Il reso si fa su merce uscita e non ancora tornata: senza, è spento. */
export function haConsegnato(righe) {
  return (righe || []).some((r) => rendibile(r) > 0);
}

/** Spiegazione sulla card del reso, spenta o accesa. */
export function titoloCausale(value, consegnato) {
  if (value !== "reso") return undefined;
  return consegnato
    ? "Merce consegnata che torna in magazzino"
    : "Niente da rendere: nessuna merce uscita, o già tornata tutta";
}

/**
 * Col reso le quantità sono quelle che si possono ancora rendere, non il
 * residuo da consegnare. L'etichetta dice quanto è già tornato. Modifica le
 * righe della modale sul posto.
 */
export function righePerReso(righe) {
  (righe || []).forEach((r) => {
    const max = rendibile(r);
    const uscito = Math.floor(Number(r.deliveredQty) || 0);
    const reso = Math.floor(Number(r.returnedQty) || 0);
    r.quantity = max;
    r.editableQty = max;
    r.selected = max > 0;
    if (reso > 0) {
      r.deliveryLabel = `Reso ${reso}/${uscito}`;
      r.deliveryFull = false;
      r.deliveryReso = true;
    }
  });
}

/**
 * L'indirizzo serve dove la merce va dal cliente: il DDT standard e la
 * consegna di PLUS. Il ritiro è in sede, il reso è merce che torna.
 */
export function chiedeIndirizzo({ standard, causale }) {
  return Boolean(standard) || causale === "consegna";
}

/**
 * Cosa scrivere sul PDF alla voce indirizzo; `null` = lasciare quello che
 * arriva dall'API (il reso).
 */
export function indirizzoPdf({ causale, indirizzo }) {
  if (causale === "reso") return null;
  if (causale === "ritiro") return RITIRO_IN_SEDE;
  return indirizzo;
}

/** Nome del file: consegne e ritiri sono numerati, ce ne può essere più d'uno. */
export function nomeFileDdt(ddt) {
  if (!ddt?.quoteId) return `DDT_N_${ddt?.id}.pdf`;
  const causale = String(ddt.causale || "").toUpperCase();
  return ddt.sequence != null
    ? `DDT_${ddt.quoteId}_${causale}_${ddt.sequence}.pdf`
    : `DDT_${ddt.quoteId}_${causale}.pdf`;
}
